// App.jsx – Juxtra landing with Live Demo & AI background (Key Features and Live Demo swapped, import path updated)
import React, { useState, useEffect } from 'react';
import AIConcept from './ai-concept.png';

/**
 * Juxtra logo component (inline SVG)
 */
const JuxtraLogo = ({ className = 'w-10 h-10' }) => (
  <svg
    className={className}
    viewBox="0 0 220 220"
    xmlns="http://www.w3.org/2000/svg"
    aria-labelledby="logoTitle"
    fill="none"
  >
    <title id="logoTitle">Juxtra logo</title>
    <defs>
      <linearGradient id="jx-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00BFFF" />
        <stop offset="50%" stopColor="#5c63ff" />
        <stop offset="100%" stopColor="#8A2BE2" />
      </linearGradient>
    </defs>
    <path d="M110 40 V140 Q110 190 60 190" stroke="url(#jx-grad)" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="110" y1="150" x2="150" y2="100" stroke="url(#jx-grad)" strokeWidth="10" strokeLinecap="round" />
    <circle cx="110" cy="40" r="20" fill="url(#jx-grad)" />
    <circle cx="150" cy="100" r="12" fill="url(#jx-grad)" />
  </svg>
);

/** Generic Section wrapper */
const Section = ({ id, title, children, bg = 'bg-white' }) => (
  <section id={id} className={`${bg} py-20 px-6 lg:px-8`}>
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl font-extrabold mb-8 text-center lg:text-left">{title}</h2>
      {children}
    </div>
  </section>
);

/** Demo placeholder */
const Demo = () => {
  const steps = [
    'Connecting to Service A...',
    'Mapping dependency B → C...',
    'Analyzing blast radius...',
    'AI suggests optimization!'
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setStep((s) => (s + 1) % steps.length), 2000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="max-w-xl mx-auto">
      <p className="text-lg font-medium text-center mb-4 text-indigo-700">{steps[step]}</p>
      <div className="h-56 bg-white border border-gray-200 rounded-2xl shadow-inner flex items-center justify-center">
        <div className="flex space-x-2 text-4xl">
          {steps.map((_, i) => <span key={i} className={step === i ? 'animate-bounce' : ''}>●</span>)}
        </div>
      </div>
    </div>
  );
};

export default function App() {
  return (
    <div className="font-sans text-gray-900 antialiased scroll-smooth">
      {/* NAVBAR */}
      <header className="fixed inset-x-0 top-0 z-50 bg-white/70 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 lg:px-8 py-3">
          <a href="#home" className="flex items-center gap-2">
            <JuxtraLogo className="w-12 h-12" />
            <span className="font-bold text-2xl">Juxtra</span>
          </a>
          <nav className="hidden md:flex gap-6 text-sm font-medium">
            {['product','demo','features','pricing','contact'].map((link) => (
              <a key={link} href={`#${link}`} className="hover:text-indigo-600 capitalize">{link}</a>
            ))}
          </nav>
          <a href="#contact" className="hidden md:inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow">Get Demo</a>
        </div>
      </header>
      <div className="h-16" />
      {/* HERO */}
      <section id="home" className="relative overflow-hidden pt-24 pb-32">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${AIConcept})` }} />
        <div className="absolute inset-0 bg-black opacity-40" />
        <div className="relative max-w-4xl mx-auto text-center px-6 lg:px-8 text-white">
          <JuxtraLogo className="w-32 h-32 mx-auto mb-6" />
          <h1 className="text-5xl font-extrabold tracking-tight mb-4">Where Services Meet</h1>
          <p className="text-lg mb-8">Juxtra visualises every microservice dependency in real time—helping teams pinpoint risks before they become outages.</p>
          <a href="#contact" className="inline-block bg-indigo-500 hover:bg-indigo-600 text-white font-semibold px-8 py-4 rounded-lg shadow-lg transition">Request Early Access</a>
        </div>
      </section>
      {/* PRODUCT OVERVIEW */}
      <Section id="product" title="The Platform">
        <p className="text-gray-600 max-w-3xl mx-auto text-center">Our unified topology engine ingests live traffic, service registries, and observability data to map how everything in your stack talks to everything else.</p>
      </Section>
      {/* WHO USES JUXTRA */}
      <Section id="audience" title="Who Uses Juxtra?" bg="bg-white">
        <ul className="space-y-3 max-w-2xl mx-auto text-gray-700 list-disc list-inside">
          {[
            'DevOps & SRE teams seeking end-to-end visibility',
            'IT Operations & Infrastructure teams ensuring uptime',
            'CTOs & Engineering Leaders guiding strategy',
            'Solutions Architects designing resilient systems',
            'Security & Cyber Teams pinpointing risk areas',
          ].map((persona) => (
            <li key={persona}>{persona}</li>
          ))}
        </ul>
      </Section>
      {/* KEY FEATURES */}
      <Section id="features" title="Key Features" bg="bg-gray-50">
        <ul className="space-y-4 max-w-2xl mx-auto text-gray-700 list-disc list-inside">
          {[
            'Instant Alerts on Service Disruptions',
            'Interactive Service Dependency Visualization',
            'Executive Dashboard & Custom Reports',
            'Comprehensive Impact Analysis & Risk Insights'
          ].map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      </Section>
      {/* LIVE DEMO */}
      <Section id="demo" title="Live Demo"><Demo /></Section>
      {/* PRICING */}
      <Section id="pricing" title="Pricing">
        <div className="flex flex-col md:flex-row gap-8 justify-center">
          {[{tier:'Starter',price:'$0',desc:'Up to 10 services'},{tier:'Growth',price:'$299',desc:'Up to 200 services'},{tier:'Enterprise',price:'Custom',desc:'Unlimited'}].map(({tier,price,desc})=>(
            <div key={tier} className="flex-1 bg-gray-50 rounded-2xl shadow p-8 text-center">
              <h3 className="text-xl font-semibold mb-2">{tier}</h3>
              <p className="text-4xl font-extrabold mb-2">{price}</p>
              <p className="text-gray-500 mb-6">{desc}</p>
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md font-medium">Choose</button>
            </div>
          ))}
        </div>
      </Section>
      {/* CONTACT */}
      <Section id="contact" title="Get in Touch" bg="bg-gray-50">
        <div className="max-w-lg mx-auto text-center">
          <a
            href="mailto:bzsweed@gmail.com"
            className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Contact Us via Email
          </a>
        </div>
      </Section>
      {/* FOOTER */}
      <footer className="bg-white py-8 border-t border-gray-200 text-center text-sm text-gray-500">© {new Date().getFullYear()} Juxtra • All rights reserved</footer>
    </div>
  );
}
